import { useEffect, useState } from 'react';
import { Box, Tab, Theme, Typography, Tabs } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { useLocation, useNavigate } from 'react-router-dom';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import { StatusChip } from '../../components/StatusChip';
import useSnackBar from '../../hooks/useSnackBar';
import appTheme from '../../theme';
import EditConfigurationView from './EditConfigurationView';
import { getAutoSweepConfig } from '../../api/bankingApi/bankingApi';
import {
  AutoSweepTrigger,
  AutoSweepAmount,
  AutoSweepConfig,
} from './types';
import { UnrestrictedAccountShort } from '../../types';
import AutoSweepTransactionsTable from './AutoSweepTransactionsTable';
import { numberToUSDCurrency } from '../../helpers/numberToUSDCurrency';
import { getUnrestrictedAccounts } from '../../api/firmsApi/unrestrictedAccountsApi';
import ViewCommonButton from '../../components/buttons/ViewCommonButton';
import { useHasPermission } from '../../hooks/useHasPermission';
import moment from 'moment';



type AutoSweepTab = 'configuration' | 'transactions';
type SweepCardId = 'revenueRecognition' | 'expenseReimbursement';


const useStyles = makeStyles()((theme: Theme) => ({
  page: {
    padding: '32px',
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
  },
  breadcrumbRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '10px',
    fontSize: '16px',
    marginBottom: '12px',
  },
  pageTitle: {
    fontSize: '28px',
    fontWeight: 600,
    fontFamily: 'Poppins',
    color: theme.palette.common.white,
  },
  description: {
    marginTop: '8px',
    color: theme.palette.secondary.main,
    fontSize: '18px',
    lineHeight: 1.4,
    fontFamily: 'Poppins',
  },
  alertBox: {
    marginTop: '32px',
    backgroundColor: theme.palette.info.dark,
    color: theme.palette.common.white,
    borderLeft: `7px solid ${theme.palette.info.main}`,
    borderRadius: '5px',
    padding: '18px 20px',
    fontSize: '17px',
    fontFamily: 'Poppins',
  },
  card: {
    marginTop: '32px',
    backgroundColor: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    padding: '32px',
    color: theme.palette.common.white,
  },
  cardTitle: {
    fontSize: '18px',
    fontWeight: 700,
    fontFamily: 'Poppins',
  },
  currentBalance: {
    fontSize: '14px',
    color: theme.palette.secondary.main,
    fontFamily: 'Poppins',
    marginTop: '6px',
  },
  autoSweepRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '8px',
    marginTop: '8px',
  },
  cardSubHeading: {
    fontSize: '16px',
    fontWeight: 500,
    fontFamily: 'Poppins',
  },
  tabsContainer: {
    marginTop: '12px',
    marginBottom: '30px',

    '& .MuiTabs-root': {
      minHeight: 'auto',
    },

    '& .MuiTab-root': {
      color: theme.palette.text.disabled,
      fontSize: '16px',
      fontWeight: 600,
      fontFamily: 'Poppins',
      textTransform: 'capitalize',
      minHeight: '48px',

      '&.Mui-selected': {
        color: theme.palette.common.white,
      },
    },

    '& .MuiTabs-indicator': {
      backgroundColor: theme.palette.info.main,
    },
  },
  configGrid: {
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '28px 80px',
    maxWidth: '1100px',

    '@media (max-width: 800px)': {
      gridTemplateColumns: '1fr',
    },
  },
  fieldBlock: {
    display: 'flex',
    flexDirection: 'column',
    gap: '6px',
  },
  fieldLabel: {
    fontSize: '16px',
    fontWeight: 700,
    fontFamily: 'Poppins',
  },
  fieldValue: {
    fontSize: '16px',
    color: theme.palette.secondary.main,
    fontFamily: 'Poppins',
  },
  editButton: {
    marginTop: '34px',
    height: '52px',
    width: 'fit-content',
  },

  transactionsToolbar: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '18px',
    gap: '16px',
  },
  tableContainer: {
    background: 'transparent',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    overflow: 'auto',
    fontFamily: 'Poppins',

    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },

    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  tableHeaderCell: {
    background: theme.palette.primary.light,

    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: `1px solid ${theme.palette.secondary.dark}`,
      fontWeight: 500,
      fontFamily: 'Poppins',
      fontSize: '14px',
      padding: '14px 16px',
    },
  },
  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: `1px solid ${theme.palette.secondary.dark}`,
      fontWeight: 400,
      fontFamily: 'Poppins',
      fontSize: '14px',
      padding: '16px',
    },
  },
  pagination: {
    marginTop: '24px',
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    color: theme.palette.secondary.main,
    fontSize: '14px',
  },
  pageControls: {
    display: 'flex',
    alignItems: 'center',
    gap: '12px',
  },
  pageButton: {
    minWidth: '40px',
    height: '40px',
    borderRadius: '6px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    color: theme.palette.secondary.main,

    '&.Mui-disabled': {
      opacity: 0.4,
      color: theme.palette.secondary.main,
    },
  },
  statusChip: {
    padding: '6px 16px',
    borderRadius: '20px',
    fontFamily: 'Poppins',
    whiteSpace: 'nowrap',
    backgroundColor: theme.palette.secondary.dark,
    width: 'fit-content',
  },
}));

const dayOfWeekMap: Record<number, string> = {
  0: 'Sunday',
  1: 'Monday',
  2: 'Tuesday',
  3: 'Wednesday',
  4: 'Thursday',
  5: 'Friday',
  6: 'Saturday',
};


const getAmountLabel = (amount?: AutoSweepAmount) => {
  if (!amount?.type) return '-';

  if (amount.type === 'FULL') {
    return 'Full credited amount';
  }
  return amount.type;
};

const getTriggerLabel = (trigger?: AutoSweepTrigger | null) => {
  if (!trigger) return '-';

  // if (trigger.mode === 'IMMEDIATE') {
  //   return 'Immediately on fund realization';
  // }

  if (trigger.mode === 'SCHEDULED') {
    const schedule = trigger.schedule;

    const dayLabel =
      typeof schedule?.dayOfWeek === 'number'
        ? dayOfWeekMap[schedule.dayOfWeek] || `Day ${schedule.dayOfWeek}`
        : '-';

    // const hour =
    //   typeof schedule?.hour === 'number'
    //     ? String(schedule.hour).padStart(2, '0')
    //     : '00';

    // const minute =
    //   typeof schedule?.minute === 'number'
    //     ? String(schedule.minute).padStart(2, '0')
    //     : '00';

    return `Weekly ${dayLabel}`;
  }

  return '-';
};

const getLastSweepLabel = (dateTime?: string | null) => {
  if (!dateTime) return 'No sweep yet';

  return `${moment(dateTime).format('MMM D, YYYY h:mm A')} — Completed`;
};

const AutoSweepPage = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const location = useLocation();
  const { state } = location;
  const { setAlert } = useSnackBar();
  const firmId = localStorage.getItem('firmId');
  const canEdit = useHasPermission('Banking', 'Edit');

  const autoSweepData = state || null;
  const [isConfigLoading, setIsConfigLoading] = useState(false);
  const [autoSweepConfig, setAutoSweepConfig] = useState<AutoSweepConfig | null>(null);
  const [unrestrictedAccounts, setUnrestrictedAccounts] = useState<UnrestrictedAccountShort[]>([]);
  const [destinationAccount, setDestinationAccount] = useState<string | null>(null);

  // Restore the tab from the URL so that navigating back from a batch details page
  // (which pushes a new route) returns to whichever tab was active, instead of
  // always resetting to Configuration.
  const initialTab: AutoSweepTab =
    new URLSearchParams(location.search).get('tab') === 'transactions'
      ? 'transactions'
      : 'configuration';

  const [activeTabs, setActiveTabs] = useState<Record<SweepCardId, AutoSweepTab>>({
    revenueRecognition: initialTab,
    expenseReimbursement: initialTab,
  });

  const [editModes, setEditModes] = useState<Record<SweepCardId, boolean>>({
    revenueRecognition: false,
    expenseReimbursement: false,
  });

  // transaction filters and data are handled inside AutoSweepTransactionsTable

  useEffect(() => {
    if (!autoSweepData?.accountType) return;

    setIsConfigLoading(true);

    const payload = {
      id: Number(firmId),
      sweepAccountType: autoSweepData.accountType,
    };

    getAutoSweepConfig(payload)
      .then((res) => {
        if (res.data) {
          setAutoSweepConfig(res.data);
        }
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message:
            error.response?.data?.message ||
            'Failed to load auto-sweep configuration.',
          type: 'error',
        }));
      })
      .finally(() => {
        setIsConfigLoading(false);
      });
  }, [autoSweepData?.accountType, setAlert]);

  useEffect(() => {

    getUnrestrictedAccounts({
      page: 1,
      size: 100,
      count: true,
    })
      .then((res) => {
        if (res.data?.resultSet) {
          const verifiedAccounts = res.data.resultSet.filter(
            (account: UnrestrictedAccountShort) => account.isEwsVerified === true
          );
          setUnrestrictedAccounts(verifiedAccounts);

          // Find the destination account from the list
          if (autoSweepConfig?.destinationAccountId) {
            const foundAccount = verifiedAccounts.find(
              (account: UnrestrictedAccountShort) => account.id === autoSweepConfig.destinationAccountId
            );
            setDestinationAccount(foundAccount?.name || null);
          } else {
            setDestinationAccount(null);
          }
        }
      })
      .catch((error) => {
        setAlert((prev) => ({
          ...prev,
          message:
            error.response?.data?.message ||
            'Failed to load trust accounts.',
          type: 'error',
        }));
      })

  }, [autoSweepConfig?.destinationAccountId, setAlert]);



  const sweepCardId: SweepCardId =
    autoSweepData?.accountType === 'EXP'
      ? 'expenseReimbursement'
      : 'revenueRecognition';

  const sweepCardTitle =
    autoSweepData?.accountType === 'EXP'
      ? 'Expense Reimbursement'
      : 'Revenue Recognition (Attorney Fees)';

  const sweepCards: Array<{
    id: SweepCardId;
    title: string;
    balance: string;
    config?: AutoSweepConfig | null;
    lastSweep?: string | null;
  }> = [
      {
        id: sweepCardId,
        title: sweepCardTitle,
        balance: autoSweepData?.accountData?.balance || '$0.00',
        config: autoSweepConfig,
        lastSweep: autoSweepConfig?.lastSweep,
      },
    ];


  const handleTabChange = (cardId: SweepCardId, tabValue: AutoSweepTab) => {
    setActiveTabs((prev) => ({
      ...prev,
      [cardId]: tabValue,
    }));

    setEditModes((prev) => ({
      ...prev,
      [cardId]: false,
    }));

    // Reflect the tab in this entry's URL (replace, not push) so that a later push to
    // the batch details page — and the browser/in-app back button from it — lands back
    // on this same tab instead of the default Configuration tab.
    navigate(
      {
        pathname: location.pathname,
        search: tabValue === 'transactions' ? '?tab=transactions' : '',
      },
      { replace: true, state },
    );
  };

  const handleEditClick = (cardId: SweepCardId) => {
    setEditModes((prev) => ({
      ...prev,
      [cardId]: true,
    }));
  };

  const handleCancelEdit = (cardId: SweepCardId) => {
    setEditModes((prev) => ({
      ...prev,
      [cardId]: false,
    }));
  };




  return (
    <Box className={classes.page}>
      <Box className={classes.breadcrumbRow}>
        <ArrowBackIcon
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/banking/accounts')}
        />
        <Typography className={classes.pageTitle}>Automatic Sweep</Typography>
      </Box>

      {/* <Typography className={classes.description}>
        Automatically move attorney fees and reimbursed expenses from your internal
        accounts to your operating bank account the moment they are credited.
      </Typography>

      <Box className={classes.alertBox}>
        Sweeps run automatically when funds are credited. Configure a destination per
        source account below. The full credited amount is swept (partial sweep coming
        later).
      </Box> */}

      {isConfigLoading ? (
        <Box className={classes.card}>
          <Typography className={classes.fieldValue}>
            Loading auto-sweep configuration...
          </Typography>
        </Box>
      ) : (
        sweepCards.map((card) => {
          const activeTab = activeTabs[card.id];
          const isEditMode = editModes[card.id];

          const isEnabled = card.config?.enabled ?? false;

          const autoSweepChip = isEnabled
            ? { label: 'Auto-Sweep: On', color: appTheme.palette.success.main }
            : { label: 'Auto-Sweep: Off', color: appTheme.palette.error.main };

          return (
            <Box className={classes.card} key={card.id}>
              <Typography className={classes.cardTitle}>
                {card.title}
              </Typography>

              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                }}
              >
                <Typography className={classes.currentBalance}>
                  Current balance · {numberToUSDCurrency(card.balance)}
                </Typography>

                <Box className={classes.autoSweepRow}>
                  <StatusChip
                    label={autoSweepChip.label}
                    color={autoSweepChip.color}
                  />
                </Box>
              </Box>

              <Box className={classes.tabsContainer}>
                <Tabs
                  value={activeTab}
                  onChange={(_, newValue) =>
                    handleTabChange(card.id, newValue as AutoSweepTab)
                  }
                  indicatorColor='primary'
                  textColor='primary'
                >
                  <Tab label='Configuration' value='configuration' />
                  <Tab label='Transactions' value='transactions' />
                </Tabs>
              </Box>

              {activeTab === 'configuration' ? (
                isEditMode ? (
                  <EditConfigurationView
                    autoSweepData={autoSweepData}
                    autoSweepConfig={autoSweepConfig}
                    unrestrictedAccounts={unrestrictedAccounts}
                    // autoSweepConfig={card.config}
                    onCancel={() => handleCancelEdit(card.id)}
                    onSave={(updatedConfig: AutoSweepConfig) => {
                      setAutoSweepConfig(updatedConfig);
                      handleCancelEdit(card.id);
                    }}
                  />
                ) : (
                  <>
                    <Box className={classes.configGrid}>
                      <Box className={classes.fieldBlock}>
                        <Typography className={classes.fieldLabel}>
                          Auto-Sweep
                        </Typography>
                        <Typography className={classes.fieldValue}>
                          {isEnabled ? 'Enabled' : 'Disabled'}
                        </Typography>
                      </Box>

                      <Box className={classes.fieldBlock}>
                        <Typography className={classes.fieldLabel}>
                          Destination account
                        </Typography>
                        <Typography className={classes.fieldValue}>
                          {destinationAccount || '-'}
                        </Typography>
                      </Box>

                      <Box className={classes.fieldBlock}>
                        <Typography className={classes.fieldLabel}>
                          Sweep timing
                        </Typography>
                        <Typography className={classes.fieldValue}>
                          {getTriggerLabel(card.config?.trigger)}
                        </Typography>
                      </Box>

                      <Box className={classes.fieldBlock}>
                        <Typography className={classes.fieldLabel}>
                          Sweep amount
                        </Typography>
                        <Typography className={classes.fieldValue}>
                          {getAmountLabel(card.config?.amount)}
                        </Typography>
                      </Box>

                      <Box className={classes.fieldBlock}>
                        <Typography className={classes.fieldLabel}>
                          Current status
                        </Typography>

                        <Box className={classes.statusChip} >
                          {(card.config?.trigger?.mode) === 'IMMEDIATE' ? 'Immediate' : (card.config?.trigger?.mode) === 'SCHEDULED' ? 'Scheduled' : '-'}
                        </Box>
                      </Box>

                      <Box className={classes.fieldBlock}>
                        <Typography className={classes.fieldLabel}>
                          Last sweep
                        </Typography>
                        <Typography className={classes.fieldValue}>
                          {getLastSweepLabel(card.lastSweep)}
                        </Typography>
                      </Box>
                    </Box>
                    
                   {canEdit &&
                    <Box className={classes.editButton}>
                      <ViewCommonButton
                        type="editAutoSweep"
                        onClick={() => handleEditClick(card.id)} />
                    </Box>}

                  </>
                )
              ) : (
                <AutoSweepTransactionsTable
                  cardId={card.id}
                  accountType={autoSweepData?.accountType}
                />
              )}
            </Box>
          );
        })
      )}
    </Box>
  );
};

export default AutoSweepPage;


